import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { formatCurrency } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { SummaryRow } from '@/components/ui/detail';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import {
  useCreateRoyaltySchemeMutation,
  useGenerateRoyaltyStatementMutation,
  useRoyaltySchemesQuery,
  useRoyaltyStatementsQuery,
  useUpdateRoyaltySchemeMutation,
  type BrandDetail,
  type RoyaltyScheme,
  type RoyaltyStatement,
} from '../brandsApi';
import { formatPeriodDate, messageOf } from '../lib';

/**
 * ADR-0023 §7 — royalty calculation INPUTS, not an invoicing system (no
 * dunning, no payment capture). `royalty.view` (read) and `royalty.configure`
 * (write) are deliberately separate keys — a franchisee branch manager holds
 * only the first (`roles.ts`'s `BRANCH_MANAGER` comment), so they read what
 * their own branch owes without holding the authority to change the rate or
 * generate a new figure.
 *
 * A red-team pass (`redteam/rt-void-revenue`) found the `revenue` measure had
 * never joined `charges`, so a charge voided through `ChargesService.void`
 * still counted toward a franchisee's billed royalty — money never collected.
 * That is fixed at the fact-source level the semantic engine already uses,
 * not re-implemented here: this screen never recomputes a royalty client
 * side, and every figure below is the server's own `Decimal` string, rendered
 * through `formatCurrency` — never `parseFloat`, never arithmetic.
 *
 * Every statement shows its own basis, period and the rate that was actually
 * snapshotted onto it (not whatever the scheme says today) — a royalty
 * statement is an invoice to a real franchisee, and a bare total is
 * unauditable.
 */
export default function RoyaltyPanel({ brand }: { brand: BrandDetail }) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const canConfigure = can('royalty.configure');

  const [schemeDialog, setSchemeDialog] = useState<RoyaltyScheme | null | undefined>(undefined);
  const [generateOpen, setGenerateOpen] = useState(false);

  const schemesQuery = useRoyaltySchemesQuery(brand.id);
  const statementsQuery = useRoyaltyStatementsQuery(brand.id);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-start justify-between">
          <div>
            <CardTitle>{t('brands.royalty.schemesTitle')}</CardTitle>
            <CardDescription>{t('brands.royalty.schemesSubtitle')}</CardDescription>
          </div>
          {canConfigure && (
            <Button size="sm" onClick={() => setSchemeDialog(null)}>
              <Plus className="h-4 w-4" />
              {t('brands.royalty.newScheme')}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {schemesQuery.isLoading ? (
            <LoadingState />
          ) : schemesQuery.error ? (
            <ErrorState
              message={messageOf(schemesQuery.error, t('brands.royalty.schemesLoadFailed'))}
              onRetry={() => schemesQuery.refetch()}
            />
          ) : (schemesQuery.data ?? []).length === 0 ? (
            <EmptyState
              title={t('brands.royalty.noSchemes')}
              hint={canConfigure ? t('brands.royalty.noSchemesHint') : undefined}
              action={
                canConfigure ? (
                  <Button size="sm" onClick={() => setSchemeDialog(null)}>
                    {t('brands.royalty.newScheme')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <TableWrap>
              <Table>
                <THead>
                  <TR>
                    <TH>{t('brands.royalty.columnName')}</TH>
                    <TH>{t('brands.royalty.columnBasis')}</TH>
                    <TH className="text-end tabular-nums">{t('brands.royalty.columnRate')}</TH>
                    <TH>{t('brands.royalty.columnEffective')}</TH>
                    <TH>{t('brands.royalty.columnStatus')}</TH>
                    {canConfigure && <TH />}
                  </TR>
                </THead>
                <tbody>
                  {(schemesQuery.data ?? []).map((scheme) => (
                    <TR
                      key={scheme.id}
                      className={canConfigure ? 'cursor-pointer' : undefined}
                      onClick={canConfigure ? () => setSchemeDialog(scheme) : undefined}
                    >
                      <TD className="font-medium">{scheme.name}</TD>
                      <TD>{t(`brands.royalty.basis.${scheme.basis}`)}</TD>
                      <TD className="text-end tabular-nums">{scheme.ratePercent}%</TD>
                      <TD>{formatPeriodDate(scheme.effectiveFrom, i18n.language)}</TD>
                      <TD>
                        <Badge tone={scheme.isActive ? 'success' : 'neutral'}>
                          {scheme.isActive ? t('common.active') : t('common.inactive')}
                        </Badge>
                      </TD>
                      {canConfigure && <TD className="text-end text-accent">{t('common.edit')}</TD>}
                    </TR>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-start justify-between">
          <div>
            <CardTitle>{t('brands.royalty.statementsTitle')}</CardTitle>
            <CardDescription>{t('brands.royalty.statementsSubtitle')}</CardDescription>
          </div>
          {canConfigure && (schemesQuery.data ?? []).length > 0 && (
            <Button size="sm" onClick={() => setGenerateOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('brands.royalty.generate')}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {statementsQuery.isLoading ? (
            <LoadingState />
          ) : statementsQuery.error ? (
            // ADR-0023's own money warning: under RLS an unbound read answers
            // zero rows rather than erroring, so a franchisor seeing "no
            // royalties due" because a query broke would be a money bug
            // wearing an empty state's clothes. This branch is what keeps
            // that from happening — a failed read is never rendered as "none
            // generated yet".
            <ErrorState
              message={messageOf(statementsQuery.error, t('brands.royalty.statementsLoadFailed'))}
              onRetry={() => statementsQuery.refetch()}
            />
          ) : (statementsQuery.data ?? []).length === 0 ? (
            <EmptyState
              title={t('brands.royalty.noStatements')}
              hint={
                canConfigure
                  ? t('brands.royalty.noStatementsHint')
                  : t('brands.royalty.noStatementsHintReadOnly')
              }
              action={
                canConfigure && (schemesQuery.data ?? []).length > 0 ? (
                  <Button size="sm" onClick={() => setGenerateOpen(true)}>
                    {t('brands.royalty.generate')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <ul className="divide-y">
              {(statementsQuery.data ?? []).map((statement) => (
                <StatementRow key={statement.id} statement={statement} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {schemeDialog !== undefined && (
        <SchemeFormDialog
          brandId={brand.id}
          scheme={schemeDialog}
          onClose={() => setSchemeDialog(undefined)}
        />
      )}
      {generateOpen && (
        <GenerateStatementDialog
          brand={brand}
          schemes={(schemesQuery.data ?? []).filter((s) => s.isActive)}
          onClose={() => setGenerateOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * One statement, with the three facts that make it auditable rather than a
 * bare number: which period, which revenue basis, and which rate produced
 * it. `ratePercent`/`basis` here are what was SNAPSHOTTED onto this
 * statement at generation time, not whatever the scheme says today.
 */
function StatementRow({ statement }: { statement: RoyaltyStatement }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  return (
    <li className="space-y-2 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-medium">{statement.branch?.name ?? statement.branchId}</p>
          <p className="text-xs text-ink-muted">
            {t('brands.royalty.period', {
              from: formatPeriodDate(statement.periodStart, locale),
              to: formatPeriodDate(statement.periodEnd, locale),
            })}
          </p>
        </div>
        <Badge tone={statement.status === 'FINAL' ? 'success' : 'neutral'}>
          {t(`brands.royalty.status.${statement.status}`)}
        </Badge>
      </div>
      <div className="grid gap-1 rounded-lg bg-subtle p-3 sm:max-w-md">
        <SummaryRow label={t('brands.royalty.columnBasis')} tone="muted">
          {t(`brands.royalty.basis.${statement.basis}`)}
        </SummaryRow>
        <SummaryRow label={t('brands.royalty.columnRate')} tone="muted">
          {statement.ratePercent}%
        </SummaryRow>
        <SummaryRow label={t('brands.royalty.salesAmount')} tone="muted">
          {formatCurrency(statement.salesAmount, statement.currency, locale)}
        </SummaryRow>
        {statement.flatFee && (
          <SummaryRow label={t('brands.royalty.flatFee')} tone="muted">
            {formatCurrency(statement.flatFee, statement.currency, locale)}
          </SummaryRow>
        )}
        <SummaryRow label={t('brands.royalty.royaltyAmount')} tone="accent">
          {formatCurrency(statement.royaltyAmount, statement.currency, locale)}
        </SummaryRow>
      </div>
    </li>
  );
}

function SchemeFormDialog({
  brandId,
  scheme,
  onClose,
}: {
  brandId: string;
  /** null creates; an existing scheme edits. */
  scheme: RoyaltyScheme | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(scheme?.name ?? '');
  const [basis, setBasis] = useState(scheme?.basis ?? 'NET_SALES');
  const [ratePercent, setRatePercent] = useState(scheme?.ratePercent ?? '');
  const [flatFee, setFlatFee] = useState(scheme?.flatFee ?? '');
  const [minimumFee, setMinimumFee] = useState(scheme?.minimumFee ?? '');
  const [effectiveFrom, setEffectiveFrom] = useState(
    scheme?.effectiveFrom.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
  );
  const [isActive, setIsActive] = useState(scheme?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);

  const [createScheme, { isLoading: creating }] = useCreateRoyaltySchemeMutation();
  const [updateScheme, { isLoading: updating }] = useUpdateRoyaltySchemeMutation();
  const saving = creating || updating;

  const submit = async () => {
    setError(null);
    const rate = Number(ratePercent);
    if (!name.trim()) return setError(t('brands.royalty.form.nameRequired'));
    if (!ratePercent || Number.isNaN(rate) || rate < 0 || rate > 100) {
      return setError(t('brands.royalty.form.rateInvalid'));
    }
    try {
      if (scheme) {
        await updateScheme({
          brandId,
          schemeId: scheme.id,
          body: {
            ratePercent,
            flatFee: flatFee || null,
            minimumFee: minimumFee || null,
            isActive,
          },
        }).unwrap();
      } else {
        await createScheme({
          brandId,
          body: {
            name: name.trim(),
            basis,
            ratePercent,
            flatFee: flatFee || undefined,
            minimumFee: minimumFee || undefined,
            effectiveFrom: new Date(effectiveFrom).toISOString(),
          },
        }).unwrap();
      }
      toast.success(t('brands.royalty.form.saved'));
      onClose();
    } catch (e) {
      setError(messageOf(e, t('brands.royalty.form.saveFailed')));
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={scheme ? t('brands.royalty.form.editTitle') : t('brands.royalty.form.createTitle')}
      className="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field label={t('brands.royalty.form.name')} required>
            <Input value={name} onChange={(e) => setName(e.target.value)} disabled={!!scheme} autoFocus />
          </Field>
        </div>
        <Field label={t('brands.royalty.columnBasis')} required>
          <Select
            value={basis}
            disabled={!!scheme}
            onChange={(e) => setBasis(e.target.value as typeof basis)}
          >
            <option value="NET_SALES">{t('brands.royalty.basis.NET_SALES')}</option>
            <option value="GROSS_SALES">{t('brands.royalty.basis.GROSS_SALES')}</option>
          </Select>
        </Field>
        <Field label={t('brands.royalty.form.effectiveFrom')} required>
          <Input
            type="date"
            value={effectiveFrom}
            disabled={!!scheme}
            onChange={(e) => setEffectiveFrom(e.target.value)}
          />
        </Field>
        <Field label={t('brands.royalty.columnRate')} required hint={t('brands.royalty.form.rateHint')}>
          <Input value={ratePercent} onChange={(e) => setRatePercent(e.target.value)} placeholder="5.0000" />
        </Field>
        <Field label={t('brands.royalty.flatFee')} hint={t('brands.royalty.form.optional')}>
          <Input value={flatFee} onChange={(e) => setFlatFee(e.target.value)} />
        </Field>
        <Field label={t('brands.royalty.form.minimumFee')} hint={t('brands.royalty.form.optional')}>
          <Input value={minimumFee} onChange={(e) => setMinimumFee(e.target.value)} />
        </Field>
        {scheme && (
          <Field label={t('brands.royalty.columnStatus')}>
            <Select value={isActive ? 'active' : 'inactive'} onChange={(e) => setIsActive(e.target.value === 'active')}>
              <option value="active">{t('common.active')}</option>
              <option value="inactive">{t('common.inactive')}</option>
            </Select>
          </Field>
        )}
        {error && <p className="text-sm text-[var(--destructive)] sm:col-span-2">{error}</p>}
      </div>
    </Dialog>
  );
}

function GenerateStatementDialog({
  brand,
  schemes,
  onClose,
}: {
  brand: BrandDetail;
  schemes: RoyaltyScheme[];
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { confirm } = useConfirm();
  const [branchId, setBranchId] = useState('');
  const [schemeId, setSchemeId] = useState(schemes[0]?.id ?? '');
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [periodStart, setPeriodStart] = useState(firstOfMonth.toISOString().slice(0, 10));
  const [periodEnd, setPeriodEnd] = useState(today.toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);

  const [generate, { isLoading: generating }] = useGenerateRoyaltyStatementMutation();

  const submit = async () => {
    setError(null);
    if (!branchId || !schemeId) {
      setError(t('brands.royalty.generateForm.selectBoth'));
      return;
    }
    const branch = brand.branches.find((b) => b.id === branchId);
    const scheme = schemes.find((s) => s.id === schemeId);

    // Outward-facing and hard to reverse — a franchisee reads this as an
    // invoice — so this confirms before calling the endpoint, and names
    // exactly what will be computed rather than a bare "are you sure".
    const ok = await confirm({
      title: t('brands.royalty.generateForm.confirmTitle'),
      body: t('brands.royalty.generateForm.confirmBody', {
        branch: branch?.name ?? branchId,
        scheme: scheme?.name ?? schemeId,
        rate: scheme?.ratePercent ?? '',
        from: formatPeriodDate(new Date(periodStart).toISOString(), i18n.language),
        to: formatPeriodDate(new Date(periodEnd).toISOString(), i18n.language),
      }),
      confirmLabel: t('brands.royalty.generate'),
    });
    if (!ok) return;

    try {
      const statement = await generate({
        brandId: brand.id,
        body: {
          branchId,
          schemeId,
          periodStart: new Date(periodStart).toISOString(),
          periodEnd: new Date(`${periodEnd}T23:59:59.999Z`).toISOString(),
        },
      }).unwrap();
      toast.success(
        t('brands.royalty.generateForm.generated', {
          amount: formatCurrency(statement.royaltyAmount, statement.currency, i18n.language),
        }),
      );
      onClose();
    } catch (e) {
      setError(messageOf(e, t('brands.royalty.generateForm.generateFailed')));
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('brands.royalty.generateForm.title')}
      description={t('brands.royalty.generateForm.description')}
      className="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={generating}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={generating}>
            {generating ? t('common.loading') : t('brands.royalty.generate')}
          </Button>
        </div>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field label={t('brands.overview.branch')} required>
            <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">{t('brands.overview.selectBranch')}</option>
              {brand.branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} ({b.code})
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label={t('brands.royalty.generateForm.scheme')} required>
            <Select value={schemeId} onChange={(e) => setSchemeId(e.target.value)}>
              {schemes.length === 0 && <option value="">{t('brands.royalty.noSchemes')}</option>}
              {schemes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} — {s.ratePercent}% ({t(`brands.royalty.basis.${s.basis}`)})
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label={t('brands.royalty.generateForm.periodStart')} required>
          <Input type="date" value={periodStart} max={periodEnd} onChange={(e) => setPeriodStart(e.target.value)} />
        </Field>
        <Field label={t('brands.royalty.generateForm.periodEnd')} required>
          <Input type="date" value={periodEnd} min={periodStart} onChange={(e) => setPeriodEnd(e.target.value)} />
        </Field>
        {error && <p className="text-sm text-[var(--destructive)] sm:col-span-2">{error}</p>}
      </div>
    </Dialog>
  );
}
